import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
  InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StarIcon from "@mui/icons-material/Star";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, horizontalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, T, inputSx, fmtCAD } from "../ServicesManager/api";

/* ── Pricing helpers ── */
const PRICING_TYPES = [
  { value: "per_event", label: "Per Event" },
  { value: "per_hour", label: "Per Hour" },
  { value: "per_person", label: "Per Person" },
  { value: "per_plate", label: "Per Plate" },
  { value: "per_guest", label: "Per Guest" },
  { value: "fixed", label: "Fixed" },
];
const pricingLabel = (t: string | null) => PRICING_TYPES.find(p => p.value === t)?.label ?? t ?? "";

/* ═══════════════════════════════════════════════════════════════════════════
   CATEGORY MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const CategoryModal: React.FC<{
  open: boolean; category: any; onSave: (d: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, category, onSave, onClose, saving }) => {
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  useEffect(() => {
    if (category) { setName(category.name || ""); setSortOrder(category.sort_order?.toString() || ""); }
    else { setName(""); setSortOrder(""); }
  }, [category, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(201,169,110,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CategoryOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {category?.id ? "Edit Category" : "New Category"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField label="Category Name *" size="small" value={name} onChange={e => setName(e.target.value)} autoFocus fullWidth sx={inputSx} />
        <TextField label="Sort Order" size="small" type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} fullWidth sx={inputSx} />
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave({ id: category?.id, name: name.trim(), sort_order: sortOrder ? Number(sortOrder) : undefined })}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   ITEM MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemModal: React.FC<{
  open: boolean; item: any; categoryId: number; subcategories: any[];
  onSave: (d: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, item, categoryId, subcategories, onSave, onClose, saving }) => {
  const [name, setName] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["per_event"]);
  const [subCatId, setSubCatId] = useState("");

  useEffect(() => {
    if (item) {
      setName(item.name || "");
      setSelectedTypes(item.supported_pricing_types ?? [item.pricing_type ?? "per_event"]);
      setSubCatId(item.category_id?.toString() || "");
    } else {
      setName(""); setSelectedTypes(["per_event"]); setSubCatId("");
    }
  }, [item, open]);

  const toggleType = (val: string) => {
    setSelectedTypes(prev => {
      if (prev.includes(val)) return prev.length <= 1 ? prev : prev.filter(v => v !== val);
      return [...prev, val];
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(139,92,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <InventoryOutlinedIcon sx={{ fontSize: 16, color: "#D6B98C" }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {item?.id ? "Edit Service Item" : "New Service Item"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField label="Item Name *" size="small" value={name} onChange={e => setName(e.target.value)} autoFocus fullWidth sx={inputSx} />
        <TextField select label="Subcategory" size="small" value={subCatId} onChange={e => setSubCatId(e.target.value)} fullWidth sx={inputSx}>
          <MenuItem value="">None</MenuItem>
          {subcategories.map((sc: any) => <MenuItem key={sc.id} value={sc.id}>{sc.name}</MenuItem>)}
        </TextField>

        {/* Pricing Types — checkbox style */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
            Pricing Types *
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
            {PRICING_TYPES.map(pt => {
              const active = selectedTypes.includes(pt.value);
              return (
                <Box key={pt.value} onClick={() => toggleType(pt.value)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.2, py: 0.8, px: 1.5, borderRadius: "10px", cursor: "pointer",
                    bgcolor: active ? "#F5EDE4" : "transparent",
                    border: `1.5px solid ${active ? "#D6B98C" : "#E0D6C8"}`,
                    transition: "all 0.1s", "&:hover": { bgcolor: active ? "#EDE3D6" : "#faf8f5" },
                  }}>
                  <Box sx={{
                    width: 20, height: 20, borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: active ? "#D6B98C" : "transparent", border: `2px solid ${active ? "#D6B98C" : T.slateLt}`,
                  }}>
                    {active && <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />}
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? T.navy : T.slate }}>{pt.label}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained"
          onClick={() => onSave({
            id: item?.id, main_category_id: categoryId,
            category_id: subCatId ? Number(subCatId) : null,
            name: name.trim(),
            pricing_type: selectedTypes[0],
            supported_pricing_types: selectedTypes,
          })}
          disabled={saving || !name.trim() || selectedTypes.length === 0}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SUBCATEGORY MODAL
   ═══════════════════════════════════════════════════════════════════════════ */
const SubcategoryModal: React.FC<{
  open: boolean; subcat: any; serviceId: number;
  onSave: (d: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, subcat, serviceId, onSave, onClose, saving }) => {
  const [name, setName] = useState("");

  useEffect(() => {
    if (subcat) setName(subcat.name || ""); else setName("");
  }, [subcat, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CategoryOutlinedIcon sx={{ fontSize: 16, color: "#D6B98C" }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {subcat?.id ? "Edit Subcategory" : "New Subcategory"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <TextField label="Subcategory Name *" size="small" value={name} onChange={e => setName(e.target.value)} autoFocus fullWidth sx={inputSx} />
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained"
          onClick={() => onSave({ id: subcat?.id, service_id: serviceId, name: name.trim() })}
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR PRICING MODAL — multi-type with dynamic price fields
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorPricingModal: React.FC<{
  open: boolean; itemId: number; vendorId: number | null; existingPrices: any[];
  supportedTypes: string[]; vendors: any[];
  onSave: (d: any) => void; onClose: () => void; saving: boolean;
}> = ({ open, itemId, vendorId: initVendorId, existingPrices, supportedTypes, vendors, onSave, onClose, saving }) => {
  const [vendorId, setVendorId] = useState("");
  const [prices, setPrices] = useState<Record<string, { vendor_price: string; dcc_price: string }>>({});

  const isEdit = !!initVendorId;

  useEffect(() => {
    if (!open) return;
    setVendorId(initVendorId?.toString() || "");
    const p: Record<string, { vendor_price: string; dcc_price: string }> = {};
    if (existingPrices.length > 0) {
      existingPrices.forEach((ep: any) => {
        p[ep.pricing_type] = { vendor_price: ep.vendor_price?.toString() || "", dcc_price: ep.dcc_price?.toString() || "" };
      });
    } else {
      // New vendor — initialize with supported types
      (supportedTypes.length > 0 ? supportedTypes : ["per_event"]).forEach(t => {
        p[t] = { vendor_price: "", dcc_price: "" };
      });
    }
    setPrices(p);
  }, [open, initVendorId, existingPrices, supportedTypes]);

  const activeTypes = Object.keys(prices);
  const toggleType = (t: string) => {
    setPrices(prev => {
      if (prev[t]) {
        if (Object.keys(prev).length <= 1) return prev; // keep at least one
        const { [t]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [t]: { vendor_price: "", dcc_price: "" } };
    });
  };
  const setField = (type: string, field: "vendor_price" | "dcc_price", val: string) => {
    setPrices(prev => ({ ...prev, [type]: { ...prev[type], [field]: val } }));
  };

  const canSave = vendorId && activeTypes.length > 0 && activeTypes.every(t => prices[t].vendor_price && prices[t].dcc_price);

  const handleSave = () => {
    const entries = activeTypes.map(t => ({
      pricing_type: t,
      vendor_price: Number(prices[t].vendor_price),
      dcc_price: Number(prices[t].dcc_price),
    }));
    onSave({ item_id: itemId, vendor_id: Number(vendorId), prices: entries });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ px: 3, py: 2, background: `linear-gradient(135deg, ${T.navy} 0%, #1a3550 100%)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: "rgba(45,106,53,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <StorefrontOutlinedIcon sx={{ fontSize: 16, color: T.greenTx }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.white }}>
            {isEdit ? "Edit Vendor Pricing" : "Add Vendor"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Vendor selector */}
        <TextField select label="Vendor *" size="small" value={vendorId} onChange={e => setVendorId(e.target.value)}
          disabled={isEdit} fullWidth sx={inputSx}>
          <MenuItem value="" disabled>Select vendor...</MenuItem>
          {vendors.map((v: any) => <MenuItem key={v.id} value={v.id}>{v.name}{v.company_name ? ` (${v.company_name})` : ""}</MenuItem>)}
        </TextField>

        {/* Pricing type checkboxes */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>
            Pricing Types
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {PRICING_TYPES.map(pt => {
              const active = pt.value in prices;
              return (
                <Chip key={pt.value} size="small"
                  icon={active ? <CheckIcon sx={{ fontSize: "12px !important", color: "#5A4633 !important" }} /> : undefined}
                  label={pt.label} onClick={() => toggleType(pt.value)}
                  sx={{
                    height: 26, fontSize: 11, fontWeight: active ? 600 : 400, cursor: "pointer",
                    bgcolor: active ? "#E8D8C3" : "transparent", color: active ? "#5A4633" : "#9ca3af",
                    border: `1.5px solid ${active ? "#D6B98C" : "#E0D6C8"}`,
                    "&:hover": { bgcolor: active ? "#E0CEB6" : "#F5EDE4" },
                  }} />
              );
            })}
          </Box>
        </Box>

        {/* Dynamic price fields per selected type */}
        {activeTypes.map(t => (
          <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#5A4633", minWidth: 80 }}>{pricingLabel(t)}</Typography>
            <TextField size="small" type="number" placeholder="Cost" value={prices[t]?.vendor_price || ""}
              onChange={e => setField(t, "vendor_price", e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ ...inputSx, flex: 1 }} />
            <TextField size="small" type="number" placeholder="Sell" value={prices[t]?.dcc_price || ""}
              onChange={e => setField(t, "dcc_price", e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ ...inputSx, flex: 1 }} />
          </Box>
        ))}

        <Typography sx={{ fontSize: 11, color: T.slateLt, fontStyle: "italic" }}>
          Best vendor is auto-selected per pricing type.
        </Typography>
      </DialogContent>
      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, borderTop: `1px solid ${T.line}` }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13, color: T.slate }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   VENDOR ROW — beige theme
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorRow: React.FC<{
  vp: any; onEdit: () => void; onDelete: () => void;
}> = ({ vp, onEdit, onDelete }) => (
  <Box sx={{
    display: "flex", alignItems: "center", justifyContent: "space-between",
    py: 0.7, px: 1.5, mx: -0.5, borderRadius: "8px",
    bgcolor: vp.is_best ? "#F5EDE4" : "transparent",
    transition: "all 0.12s",
    "&:hover": { bgcolor: vp.is_best ? "#F5EDE4" : "#faf8f5" },
    "&:hover .vendor-actions": { opacity: 1 },
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flex: 1, minWidth: 0 }}>
      {vp.is_best && <StarIcon sx={{ fontSize: 12, color: "#D6B98C" }} />}
      {vp.is_best && (
        <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#5A4633", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Best
        </Typography>
      )}
      <Typography sx={{ fontSize: 13, fontWeight: vp.is_best ? 600 : 400, color: "#2C2C2C" }}>
        {vp.vendor?.name ?? "Unknown"}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#2C2C2C", fontVariantNumeric: "tabular-nums" }}>
        {fmtCAD(Number(vp.dcc_price ?? 0))}
      </Typography>
      <Box className="vendor-actions" sx={{ display: "flex", opacity: 0, transition: "opacity 0.15s" }}>
        <IconButton size="small" onClick={onEdit} sx={{ color: "#9ca3af", p: 0.3, "&:hover": { color: "#D6B98C" } }}>
          <EditOutlinedIcon sx={{ fontSize: 13 }} />
        </IconButton>
        <IconButton size="small" onClick={onDelete} sx={{ color: "#9ca3af", p: 0.3, "&:hover": { color: T.redTx } }}>
          <DeleteOutlineIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   ITEM ROW — beige theme, right-aligned pricing chips
   ═══════════════════════════════════════════════════════════════════════════ */
const ItemCard: React.FC<{
  item: any; onEditItem: () => void; onDeleteItem: () => void;
  onAddVendor: (itemId: number) => void; onEditVendor: (vendorId: number, itemId: number) => void;
  onDeleteVendor: (vp: any) => void;
  onPricingTypesChange: (itemId: number, types: string[], activeType: string) => void;
  bestOnly: boolean;
}> = ({ item, onEditItem, onDeleteItem, onAddVendor, onEditVendor, onDeleteVendor, onPricingTypesChange, bestOnly }) => {
  const allVendors: any[] = item.vendor_pricing ?? [];
  const supported: string[] = item.supported_pricing_types ?? [item.pricing_type ?? "per_event"];
  const [activeType, setActiveType] = useState<string>(supported[0] ?? "per_event");

  useEffect(() => {
    const spt: string[] = item.supported_pricing_types ?? [item.pricing_type ?? "per_event"];
    if (!spt.includes(activeType)) setActiveType(spt[0] ?? "per_event");
  }, [item.supported_pricing_types, item.pricing_type]);

  const vendorsForType = useMemo(() => {
    const filtered = allVendors.filter((v: any) => v.pricing_type === activeType);
    return bestOnly ? filtered.filter((v: any) => v.is_best) : filtered;
  }, [allVendors, activeType, bestOnly]);

  const hasVendors = allVendors.length > 0;

  return (
    <Box sx={{ py: 1.8, "&:not(:last-child)": { borderBottom: `1px solid #EAE3D9` } }}>
      {/* ── Single row: Name | vendor count | pricing chips | edit icon ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: vendorsForType.length > 0 || !hasVendors ? 0.8 : 0 }}>
        {/* Left: name + vendor count */}
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#2C2C2C", flex: "0 0 auto" }}>{item.name}</Typography>
        {hasVendors && (
          <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>{allVendors.length} vendor{allVendors.length !== 1 ? "s" : ""}</Typography>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Right: pricing chips */}
        {supported.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.4, flexShrink: 0 }}>
            {PRICING_TYPES.map(pt => {
              const isSupported = supported.includes(pt.value);
              const isActive = activeType === pt.value;
              if (!isSupported) return null;
              return (
                <Chip key={pt.value} size="small" label={pt.label}
                  onClick={() => setActiveType(pt.value)}
                  sx={{
                    height: 24, fontSize: 10.5, fontWeight: isActive ? 600 : 400, cursor: "pointer",
                    bgcolor: isActive ? "#E8D8C3" : "transparent",
                    color: isActive ? "#5A4633" : "#9ca3af",
                    border: `1px solid ${isActive ? "#D6B98C" : "#E0D6C8"}`,
                    transition: "all 0.1s",
                    "&:hover": { bgcolor: isActive ? "#E0CEB6" : "#F5EDE4" },
                  }} />
              );
            })}
          </Box>
        )}

        {/* Edit icon */}
        <Tooltip title="Edit Pricing">
          <IconButton size="small" onClick={onEditItem} sx={{ color: "#9ca3af", p: 0.4, flexShrink: 0, "&:hover": { color: "#D6B98C" } }}>
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Vendor list ── */}
      {vendorsForType.length > 0 ? (
        <Box sx={{ pl: 1 }}>
          {vendorsForType.map((vp: any) => (
            <VendorRow key={vp.id} vp={vp}
              onEdit={() => onEditVendor(vp.vendor_id, item.id)}
              onDelete={() => onDeleteVendor(vp)} />
          ))}
        </Box>
      ) : hasVendors ? (
        <Typography sx={{ fontSize: 11, color: "#9ca3af", pl: 1, py: 0.3 }}>
          No {pricingLabel(activeType)} pricing. Switch type or add.
        </Typography>
      ) : null}

      {/* ── Add vendor ── */}
      <Box sx={{ pl: 1, pt: 0.3 }}>
        <Typography component="span" onClick={() => onAddVendor(item.id)}
          sx={{ fontSize: 11, fontWeight: 500, color: "#D6B98C", cursor: "pointer", "&:hover": { color: "#5A4633" } }}>
          + Add vendor
        </Typography>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
   SORTABLE TAB (drag handle)
   ═══════════════════════════════════════════════════════════════════════════ */
const SortableTab: React.FC<{ cat: any; isActive: boolean; onClick: () => void }> = ({ cat, isActive, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}
      sx={{
        display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 1.2, cursor: "pointer",
        borderBottom: isActive ? `3px solid ${T.gold}` : "3px solid transparent",
        color: isActive ? T.navy : T.slateLt,
        fontWeight: isActive ? 600 : 500,
        fontSize: 13, whiteSpace: "nowrap", userSelect: "none",
        transition: "all 0.15s", "&:hover": { color: T.navy },
      }}
      onClick={onClick}>
      <Box {...attributes} {...listeners} sx={{ display: "flex", alignItems: "center", cursor: "grab", color: T.slateLt, "&:hover": { color: T.gold }, mr: 0.3 }}>
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
      <CategoryOutlinedIcon sx={{ fontSize: 15 }} />
      <span>{cat.name}</span>
      <Chip label={cat.items_count ?? 0} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#F5EDE4", color: "#5A4633", ml: 0.3 }} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const VendorServicesPage: React.FC = () => {
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState(-1); // -1 = All
  const [bestOnly, setBestOnly] = useState(false);

  // Modals
  const [catModal, setCatModal] = useState<{ open: boolean; cat: any }>({ open: false, cat: null });
  const [subCatModal, setSubCatModal] = useState<{ open: boolean; subcat: any }>({ open: false, subcat: null });
  const [itemModal, setItemModal] = useState<{ open: boolean; item: any }>({ open: false, item: null });
  const [pricingModal, setPricingModal] = useState<{ open: boolean; vendorId: number | null; itemId: number }>({ open: false, vendorId: null, itemId: 0 });
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string; type: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const curCat = categories[activeTab];

  // Drag-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = categories.findIndex((c: any) => c.id === active.id);
    const newIdx = categories.findIndex((c: any) => c.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;

    const reordered = arrayMove(categories, oldIdx, newIdx);
    setCategories(reordered);

    // Keep active tab on the same category
    if (activeTab === oldIdx) setActiveTab(newIdx);
    else if (activeTab > oldIdx && activeTab <= newIdx) setActiveTab(activeTab - 1);
    else if (activeTab < oldIdx && activeTab >= newIdx) setActiveTab(activeTab + 1);

    // Persist to backend
    const payload = reordered.map((c: any, i: number) => ({ id: c.id, sort_order: i + 1 }));
    await api.post("/vendor-services/reorder-categories", { items: payload });
  }, [categories, activeTab]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    const res = await api.get("/vendor-services/categories");
    if (res.code === 200 || res.status) setCategories(res.data ?? []);
    setLoading(false);
  }, []);

  // Fetch items for active category
  const fetchItems = useCallback(async (catId: number) => {
    setItemsLoading(true);
    const res = await api.get(`/vendor-services/items/${catId}`);
    if (res.code === 200 || res.status) setItems(res.data ?? []);
    setItemsLoading(false);
  }, []);

  const fetchSubcategories = useCallback(async (serviceId: number) => {
    const res = await api.get(`/vendor-services/subcategories/${serviceId}`);
    if (res.code === 200 || res.status) setSubcategories(res.data ?? []);
  }, []);

  // Fetch all vendors (for the dropdown)
  const fetchVendors = useCallback(async () => {
    const res = await api.get("/banquet/vendors");
    if (res.code === 200 || res.status) setVendors(res.data ?? []);
  }, []);

  useEffect(() => { fetchCategories(); fetchVendors(); }, [fetchCategories, fetchVendors]);
  useEffect(() => {
    if (curCat) { fetchItems(curCat.id); fetchSubcategories(curCat.id); setActiveSubTab(-1); }
    else { setItems([]); setSubcategories([]); }
  }, [curCat, fetchItems, fetchSubcategories]);

  // Filter items by active subcategory tab
  const visibleItems = useMemo(() => {
    if (activeSubTab === -1) return items; // "All"
    const sc = subcategories[activeSubTab];
    if (!sc) return items;
    return items.filter((i: any) => i.category_id === sc.id);
  }, [items, subcategories, activeSubTab]);

  // ── CATEGORY CRUD ──
  const handleSaveCategory = async (data: any) => {
    setSaving(true);
    const res = await api.post("/vendor-services/categories", data);
    if (res.code === 200 || res.status) {
      setCatModal({ open: false, cat: null });
      fetchCategories();
    }
    setSaving(false);
  };

  // ── SUBCATEGORY CRUD ──
  const handleSaveSubcategory = async (data: any) => {
    setSaving(true);
    const res = await api.post("/vendor-services/subcategories", data);
    if (res.code === 200 || res.status) {
      setSubCatModal({ open: false, subcat: null });
      if (curCat) { fetchSubcategories(curCat.id); fetchItems(curCat.id); }
    }
    setSaving(false);
  };

  // ── ITEM CRUD ──
  const handleSaveItem = async (data: any) => {
    setSaving(true);
    const res = await api.post("/vendor-services/items", data);
    if (res.code === 200 || res.status) {
      setItemModal({ open: false, item: null });
      if (curCat) fetchItems(curCat.id);
    }
    setSaving(false);
  };

  // ── PRICING TYPES CHANGE (multi-select, async save) ──
  const handlePricingTypesChange = useCallback(async (itemId: number, types: string[], activeType: string) => {
    const item = items.find((i: any) => i.id === itemId);
    if (!item || !curCat) return;
    // Fire-and-forget async save
    api.post("/vendor-services/items", {
      id: itemId,
      main_category_id: curCat.id,
      name: item.name,
      pricing_type: activeType,
      supported_pricing_types: types,
    });
  }, [items, curCat]);

  // ── PRICING CRUD (bulk) ──
  const handleSavePricing = async (data: any) => {
    setSaving(true);
    const res = await api.post("/vendor-services/pricing-bulk", data);
    if (res.code === 200 || res.status) {
      setPricingModal({ open: false, vendorId: null, itemId: 0 });
      if (curCat) fetchItems(curCat.id);
    }
    setSaving(false);
  };

  // ── DELETE ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const endpoint =
      deleteTarget.type === "category" ? `/vendor-services/categories/${deleteTarget.id}` :
      deleteTarget.type === "subcategory" ? `/vendor-services/subcategories/${deleteTarget.id}` :
      deleteTarget.type === "item" ? `/vendor-services/items/${deleteTarget.id}` :
      `/vendor-services/pricing/${deleteTarget.id}`;
    const res = await api.del(endpoint);
    if (res.code === 200 || res.status) {
      if (deleteTarget.type === "category") {
        if (activeTab >= categories.length - 1) setActiveTab(Math.max(0, activeTab - 1));
        fetchCategories();
      } else {
        if (curCat) { fetchItems(curCat.id); fetchSubcategories(curCat.id); }
      }
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>;
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pt: 1 }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: T.navy, letterSpacing: "-0.02em" }}>
            Vendor Services
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 0.3 }}>
            Manage vendor-based services and pricing
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={() => window.location.href = "/banquet/service-catalogue"}
            sx={{ textTransform: "none", fontWeight: 500, fontSize: 12, borderColor: T.line, color: T.slate, borderRadius: "10px", border: `1px solid ${T.line}`, "&:hover": { borderColor: T.gold, bgcolor: T.peach } }}>
            View in Catalogue
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setCatModal({ open: true, cat: null })}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 2.5, boxShadow: "none", "&:hover": { bgcolor: "#1a3550" } }}>
            Add Category
          </Button>
        </Box>
      </Box>

      {categories.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: "center", borderRadius: "14px", border: `1px dashed ${T.line}` }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 48, color: T.slateLt, mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: T.slateLt, mb: 2 }}>No vendor categories yet. Create one to get started.</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCatModal({ open: true, cat: null })}
            sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: T.navy, color: T.navy, borderRadius: "10px" }}>
            Add First Category
          </Button>
        </Paper>
      ) : (
        <Box sx={{ bgcolor: T.white, borderRadius: "14px", boxShadow: "0 1px 3px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.02)", overflow: "hidden" }}>
          {/* ── Drag-sortable tab bar ── */}
          <Box sx={{ borderBottom: `1px solid ${T.line}`, px: { xs: 1, sm: 2 }, display: "flex", alignItems: "center" }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={categories.map((c: any) => c.id)} strategy={horizontalListSortingStrategy}>
                <Box sx={{ display: "flex", flex: 1, overflowX: "auto", gap: 0, alignItems: "stretch" }}>
                  {categories.map((c: any, idx: number) => (
                    <SortableTab key={c.id} cat={c} isActive={activeTab === idx} onClick={() => setActiveTab(idx)} />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 1, flexShrink: 0 }}>
              <Tooltip title="Add Category">
                <IconButton size="small" onClick={() => setCatModal({ open: true, cat: null })}
                  sx={{ color: T.gold, border: `1px dashed ${T.gold}`, width: 30, height: 30, "&:hover": { bgcolor: T.peach } }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {curCat && (
                <>
                  <Tooltip title="Edit Category"><IconButton size="small" onClick={() => setCatModal({ open: true, cat: curCat })} sx={{ color: T.slateLt, "&:hover": { color: T.gold } }}>
                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton></Tooltip>
                  <Tooltip title="Delete Category"><IconButton size="small" onClick={() => setDeleteTarget({ id: curCat.id, name: curCat.name, type: "category" })} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton></Tooltip>
                </>
              )}
            </Box>
          </Box>

          {/* ── Subcategory tabs (second row) ── */}
          {subcategories.length > 0 && (
            <Box sx={{ borderBottom: `1px solid ${T.line}`, px: 2, display: "flex", alignItems: "center", bgcolor: T.bg }}>
              <Box sx={{ display: "flex", flex: 1, overflowX: "auto", gap: 0 }}>
                <Box onClick={() => setActiveSubTab(-1)}
                  sx={{
                    px: 1.5, py: 1, fontSize: 12, fontWeight: activeSubTab === -1 ? 600 : 400, cursor: "pointer",
                    color: activeSubTab === -1 ? T.navy : T.slateLt, whiteSpace: "nowrap",
                    borderBottom: activeSubTab === -1 ? `2px solid #D6B98C` : "2px solid transparent",
                    transition: "all 0.12s", "&:hover": { color: T.navy },
                  }}>All</Box>
                {subcategories.map((sc: any, idx: number) => (
                  <Box key={sc.id} onClick={() => setActiveSubTab(idx)}
                    sx={{
                      px: 1.5, py: 1, fontSize: 12, fontWeight: activeSubTab === idx ? 600 : 400, cursor: "pointer",
                      color: activeSubTab === idx ? T.navy : T.slateLt, whiteSpace: "nowrap",
                      borderBottom: activeSubTab === idx ? `2px solid #D6B98C` : "2px solid transparent",
                      transition: "all 0.12s", "&:hover": { color: T.navy },
                    }}>{sc.name}</Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 0.3, flexShrink: 0, pr: 1 }}>
                <Tooltip title="Add Subcategory">
                  <IconButton size="small" onClick={() => setSubCatModal({ open: true, subcat: null })}
                    sx={{ color: "#D6B98C", p: 0.4, "&:hover": { bgcolor: "#D6B98C12" } }}>
                    <AddIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                {activeSubTab >= 0 && subcategories[activeSubTab] && (
                  <>
                    <IconButton size="small" onClick={() => setSubCatModal({ open: true, subcat: subcategories[activeSubTab] })}
                      sx={{ color: T.slateLt, p: 0.3, "&:hover": { color: T.gold } }}>
                      <EditOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeleteTarget({ id: subcategories[activeSubTab].id, name: subcategories[activeSubTab].name, type: "subcategory" })}
                      sx={{ color: T.slateLt, p: 0.3, "&:hover": { color: T.redTx } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* ── Tab content ── */}
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Action bar */}
            {curCat && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {subcategories.length === 0 && (
                    <Typography component="span" onClick={() => setSubCatModal({ open: true, subcat: null })}
                      sx={{ fontSize: 12, fontWeight: 500, color: "#D6B98C", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                      + Add subcategory
                    </Typography>
                  )}
                  <Typography component="span" onClick={() => setItemModal({ open: true, item: null })}
                    sx={{ fontSize: 12, fontWeight: 500, color: "#D6B98C", cursor: "pointer", "&:hover": { color: "#5A4633" } }}>
                    + Add item
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.8 }}>
                  <Chip label="All" size="small" onClick={() => setBestOnly(false)}
                    sx={{ height: 24, fontSize: 11, fontWeight: bestOnly ? 400 : 600, bgcolor: bestOnly ? T.bg : T.navy, color: bestOnly ? T.slate : T.white, cursor: "pointer" }} />
                  <Chip icon={<StarIcon sx={{ fontSize: 11 }} />} label="Best Only" size="small" onClick={() => setBestOnly(true)}
                    sx={{ height: 24, fontSize: 11, fontWeight: bestOnly ? 600 : 400, bgcolor: bestOnly ? "#fef9ec" : T.bg, color: bestOnly ? "#92710c" : T.slate, cursor: "pointer", "& .MuiChip-icon": { color: bestOnly ? T.gold : T.slateLt } }} />
                </Box>
              </Box>
            )}

            {/* Items list */}
            {itemsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} sx={{ color: T.gold }} /></Box>
            ) : visibleItems.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <InventoryOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, mb: 0.5 }} />
                <Typography sx={{ fontSize: 13, color: T.slateLt }}>No items yet.</Typography>
              </Box>
            ) : (
              <Box>
                {visibleItems.map((item: any) => (
                  <ItemCard key={item.id} item={item} bestOnly={bestOnly}
                    onEditItem={() => setItemModal({ open: true, item })}
                    onDeleteItem={() => setDeleteTarget({ id: item.id, name: item.name, type: "item" })}
                    onAddVendor={(itemId) => setPricingModal({ open: true, vendorId: null, itemId })}
                    onEditVendor={(vendorId, itemId) => setPricingModal({ open: true, vendorId, itemId })}
                    onDeleteVendor={(vp) => setDeleteTarget({ id: vp.id, name: `${vp.vendor?.name} pricing`, type: "pricing" })}
                    onPricingTypesChange={handlePricingTypesChange}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── Modals ── */}
      <CategoryModal open={catModal.open} category={catModal.cat} onSave={handleSaveCategory}
        onClose={() => setCatModal({ open: false, cat: null })} saving={saving} />

      <SubcategoryModal open={subCatModal.open} subcat={subCatModal.subcat} serviceId={curCat?.id ?? 0}
        onSave={handleSaveSubcategory} onClose={() => setSubCatModal({ open: false, subcat: null })} saving={saving} />

      <ItemModal open={itemModal.open} item={itemModal.item} categoryId={curCat?.id ?? 0}
        subcategories={subcategories}
        onSave={handleSaveItem} onClose={() => setItemModal({ open: false, item: null })} saving={saving} />

      <VendorPricingModal open={pricingModal.open} itemId={pricingModal.itemId}
        vendorId={pricingModal.vendorId}
        existingPrices={pricingModal.vendorId ? (items.find((i: any) => i.id === pricingModal.itemId)?.vendor_pricing ?? []).filter((vp: any) => vp.vendor_id === pricingModal.vendorId) : []}
        supportedTypes={(items.find((i: any) => i.id === pricingModal.itemId)?.supported_pricing_types ?? ["per_event"])}
        vendors={vendors} onSave={handleSavePricing}
        onClose={() => setPricingModal({ open: false, vendorId: null, itemId: 0 })} saving={saving} />

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 16 }}>
          <WarningAmberIcon color="error" /> Delete {deleteTarget?.type}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            Delete <strong>{deleteTarget?.name}</strong>?
            {deleteTarget?.type === "category" ? " All items and vendor pricing in this category will be removed." : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>{deleting ? "..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VendorServicesPage;
